import React, { Fragment, useMemo, useEffect, useRef } from "react";
import { Card, Table, Row, Col } from "react-bootstrap";
import moment from "moment-timezone";

const DEPLOYMENT_MAP = {
  "a0d93d27-392a-4132-8e66-61a30a39df77": "DMT",
  "c1331c8a-9459-4be6-8f8d-75ce571107f7": "Warranty",
};

// Transform dynamic deployment data using UTC logic and converting to EST for display.
function transformDynamicData(deployments) {
  console.log(
    "%cTransforming dynamic data...",
    "color: blue; font-weight: bold; font-size: 16px;"
  );
  const nowUTC = moment.utc();
  const nowEST = moment.tz("America/New_York");

  const result = {
    Warranty: {},
    DMT: {},
  };

  deployments.forEach((deployment) => {
    const colKey = DEPLOYMENT_MAP[deployment.deployment_id];
    if (!colKey) {
      console.warn(
        `Skipping unknown deployment ID: ${deployment.deployment_id}`
      );
      return;
    }
    if (Array.isArray(deployment.activity_of_runs)) {
      deployment.activity_of_runs.forEach((run) => {
        // Prefer expected_start_time if available; otherwise use flow_run_start_time.
        const timeString = run.expected_start_time || run.flow_run_start_time;
        const runMomentUTC = moment.utc(timeString);
        const runMomentEST = runMomentUTC.clone().tz("America/New_York");

        // Only include runs that fall on today's date in EST.
        if (!runMomentEST.isSame(nowEST, "day")) return;

        const runTimeLabel = runMomentEST.format("h:mm A");
        let status = run.state || "N/A";
        let runtime = run.run_duration_formatted || "-";

        // If an expected_start_time exists and the run's UTC time is in the future, mark it as "Scheduled"
        if (run.expected_start_time && runMomentUTC.isAfter(nowUTC)) {
          status = "Scheduled";
          runtime = "";
          console.log(
            `%cRun scheduled for ${runMomentEST.format("h:mm A")}: ${status}`,
            "color: purple; font-weight: bold; font-size: 16px;"
          );
        }
        result[colKey][runTimeLabel] = {
          status,
          runtime,
          displayTime: runTimeLabel,
        };
      });
    }
  });
  console.log("%cTransformation complete!", "color: green; font-weight: bold;");
  return result;
}

const ClusterPoolTwo = ({ dynamicData }) => {
  // Fallback static data if no dynamicData provided.
  const staticData = {
    Warranty: {
      "12:00 AM": {
        status: "Completed",
        runtime: "1 Hr 2 Min",
        displayTime: "12:00 AM",
      },
    },
    DMT: {},
  };

  // Use dynamic data if available.
  const data = Array.isArray(dynamicData)
    ? transformDynamicData(dynamicData)
    : staticData;

  // Set our reference timezone.
  const nowEST = moment.tz("America/New_York");

  // Generate time slots for the entire day (midnight to 11:00 PM) in EST.
  const timeSlots = useMemo(() => {
    const slots = [];
    const start = moment.tz("America/New_York").startOf("day");
    for (let i = 0; i < 24; i++) {
      slots.push(start.clone().add(i, "hours").format("h:mm A"));
    }
    return slots;
  }, []);

  const columns = [
    {
      title: "Warranty",
      schedule: "",
      dataKey: "Warranty",
    },
    {
      title: "DMT",
      schedule: "",
      dataKey: "DMT",
    },
  ];

  // Calculate the current hour in EST.
  const currentHour = nowEST.clone().startOf("hour").format("h:mm A");
  const currentRowRef = useRef(null);

  useEffect(() => {
    if (currentRowRef.current) {
      currentRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, []);

  // Helper: Map status to styles.
  const getStatusStyle = (status) => {
    if (!status) return {};
    switch (status.toLowerCase()) {
      case "completed":
        return { color: "green", fontWeight: "bold" };
      case "running":
      case "in progress":
        return { color: "blue", fontWeight: "bold" };
      case "failed":
      case "cancelled":
        return { color: "red", fontWeight: "bold" };
      case "scheduled":
        return { color: "#FFD700", fontWeight: "bold" }; // Darker yellow for scheduled status
      default:
        return {};
    }
  };

  // Retrieve run data for each column and time slot.
  const getDataForTime = (colKey, slotTime) => {
    // First, check if there's an exact match in the dynamic data.
    const exact = data[colKey]?.[slotTime];
    if (exact) return exact;

    // Otherwise, attempt to match by the hour in EST.
    const keys = Object.keys(data[colKey] || {});
    const slotHour = moment.tz(slotTime, "h:mm A", "America/New_York").hour();
    for (const key of keys) {
      const keyHour = moment.tz(key, "h:mm A", "America/New_York").hour();
      if (keyHour === slotHour) {
        return data[colKey][key];
      }
    }

    // Build a moment object for the current slot today in EST.
    const slotMoment = moment.tz(
      `${nowEST.format("M/D/YYYY")} ${slotTime}`,
      "M/D/YYYY h:mm A",
      "America/New_York"
    );

    // If this column runs every hour and the slot is in the future, mark as "Scheduled"
    if (["Warranty", "DMT"].includes(colKey)) {
      if (slotMoment.isAfter(nowEST)) {
        return {
          status: "Scheduled",
          runtime: "-",
          displayTime: slotTime,
        };
      }
    }

    // Otherwise, if it's in the past or there's just no data, return the default dash.
    return {
      status: "-",
      runtime: "-",
      displayTime: slotTime,
    };
  };

  return (
    <Fragment>
      <Row className="mt-5">
        <Col lg={12}>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <Card.Header>
              <Card.Title className="text-center" style={{ color: "#3D4465" }}>
                Cluster Pool 2
              </Card.Title>
              <div
                style={{
                  textAlign: "center",
                  fontSize: "1.1rem",
                  marginTop: "5px",
                  color: "#3D4465",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.9rem",
                    color: "#3d4465",
                  }}
                >
                  Last refreshed: {moment().format("YYYY-MM-DD HH:mm:ss")}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                <Table
                  bordered
                  style={{ borderRadius: "8px", marginBottom: 0 }}
                >
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.title}
                          colSpan={3}
                          style={{
                            textAlign: "center",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#3D4465",
                            color: "white",
                            zIndex: 4,
                            padding: "8px",
                          }}
                        >
                          <div style={{ fontWeight: "bold" }}>{col.title}</div>
                          <div
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "0.85rem",
                            }}
                          >
                            {col.schedule}
                          </div>
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {columns.map((col) => (
                        <React.Fragment key={col.title}>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: "40px",
                              backgroundColor: "#3D4465",
                              color: "white",
                              zIndex: 3,
                              padding: "8px",
                            }}
                          >
                            Runs
                          </th>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: "40px",
                              backgroundColor: "#3D4465",
                              color: "white",
                              zIndex: 3,
                              padding: "8px",
                            }}
                          >
                            Status
                          </th>
                          <th
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              top: "40px",
                              backgroundColor: "#3D4465",
                              color: "white",
                              zIndex: 3,
                              padding: "8px",
                            }}
                          >
                            Run time
                          </th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((time) => {
                      const isCurrentRow = time === currentHour;
                      const rowStyle = isCurrentRow
                        ? {
                            position: "sticky",
                            top: "80px",
                            backgroundColor: "#d1e7dd",
                            zIndex: 2,
                          }
                        : {};
                      return (
                        <tr
                          key={time}
                          style={rowStyle}
                          ref={isCurrentRow ? currentRowRef : null}
                        >
                          {columns.map((col) => {
                            const cellData = getDataForTime(col.dataKey, time);
                            const displayTime = cellData.displayTime || time;
                            return (
                              <React.Fragment key={`${col.title}-${time}`}>
                                <td style={{ textAlign: "center" }}>
                                  {displayTime}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <span style={getStatusStyle(cellData.status)}>
                                    {cellData.status === "Completed"
                                      ? "Succeeded"
                                      : cellData.status}
                                  </span>
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {cellData.runtime}
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default ClusterPoolTwo;
